import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'
import { useCurrentUser } from '@/features/auth/hooks/use-current-user'
import type { Organization } from '@/shared/types'

export interface BillingAddress {
  street?: string
  postal_code?: string
  city?: string
  country?: string
  vat_number?: string
}

export interface OrganizationWithBilling extends Omit<Organization, 'billing_address'> {
  billing_address?: BillingAddress
}

async function fetchOrganization(organizationId: string): Promise<OrganizationWithBilling | null> {
  const { data, error } = await supabase
    .from('organizations')
    .select('*')
    .eq('id', organizationId)
    .single()

  if (error) {
    if (error.code === 'PGRST116') {
      return null
    }
    throw new Error(error.message)
  }

  return data as OrganizationWithBilling
}

export function useOrganization() {
  const { user } = useCurrentUser()

  return useQuery({
    queryKey: ['organization', user?.organization_id],
    queryFn: () => fetchOrganization(user!.organization_id!),
    enabled: !!user?.organization_id,
  })
}

interface UpdateOrganizationData {
  name: string
  billing_address: BillingAddress
}

async function updateOrganization(
  organizationId: string,
  data: UpdateOrganizationData
): Promise<void> {
  const { error } = await supabase
    .from('organizations')
    .update({
      name: data.name,
      billing_address: data.billing_address,
      updated_at: new Date().toISOString(),
    })
    .eq('id', organizationId)

  if (error) {
    throw new Error(error.message)
  }
}

export function useUpdateOrganization(organizationId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateOrganizationData) => updateOrganization(organizationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] })
    },
  })
}
